import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RoomsService } from '../../../../core/services/rooms.service';
import { BlockedRoom } from '../../../../core/models/room.model';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { take, filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { PdfService } from '../../../../core/services/pdf.service';

@Component({
    selector: 'app-rooms-not-available',
    standalone: true,
    imports: [
        CommonModule, 
        FormsModule, 
        AppPrimeTable, 
        TranslateModule, 
        IconFieldModule, 
        InputIconModule, 
        InputTextModule,
        DatePickerModule,
        ButtonModule,
        TooltipModule,
        AppButton
    ],
    templateUrl: './rooms-not-available.html',
})
export class RoomsNotAvailable implements OnInit {
    blockedRooms: BlockedRoom[] = [];
    loading = false;
    isFetching = false;

    cols: PrimeTableColumn[] = [
        { field: 'office_name', header: 'rooms_field_name', sortable: true },
        { field: 'block_date_start', header: 'START_DATE', type: 'date', sortable: true },
        { field: 'block_date_end', header: 'END_DATE', type: 'date', sortable: true },
        { field: 'start_block_time', header: 'START_TIME', sortable: false },
        { field: 'end_block_time', header: 'END_TIME', sortable: false },
        { field: 'block_reason', header: 'REASON', sortable: false }
    ];

    // Pagination
    currentPage = 1;
    pageSize = 10;
    totalPages = 0;
    searchText = '';

    // Date filters
    startDate: Date | null = null;
    endDate: Date | null = null;

    private store = inject(Store);
    private userInfo$ = this.store.select(selectUserInfo);
    private translate = inject(TranslateService);
    private pdfService = inject(PdfService);

    constructor(
        private roomsService: RoomsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        // Default to the current calendar month; lazy load will trigger the
        // first fetch.
        const { start, end } = this.getCurrentMonthRange();
        this.startDate = start;
        this.endDate = end;
    }

    private getCurrentMonthRange(): { start: Date; end: Date } {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start, end };
    }

    private isSameDay(a: Date | null, b: Date): boolean {
        return !!a && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    /** True once the user has moved away from the default current-month range or typed a search — drives the "clear filters" button. */
    get hasCustomFilters(): boolean {
        const { start, end } = this.getCurrentMonthRange();
        return !!this.searchText || !this.isSameDay(this.startDate, start) || !this.isSameDay(this.endDate, end);
    }

    async printReport() {
        this.loading = true;
        this.cdr.detectChanges();

        try {
            const userInfo = (await firstValueFrom(
                this.userInfo$.pipe(
                    filter(u => !!u),
                    take(1)
                )
            )) as any;

            const branchId = userInfo?.branches_id || userInfo?.branch_id;
            if (!branchId) {
                console.error('No branch ID found for user');
                this.loading = false;
                this.cdr.detectChanges();
                return;
            }

            // Fetch with a large limit to get "complete" data for the report
            const { data } = await this.roomsService.getBlockedRooms(
                branchId,
                2000,
                0,
                this.startDate ? this.toDateOnlyString(this.startDate) : '',
                this.endDate ? this.toDateOnlyString(this.endDate) : '',
                this.searchText
            );

            const allData = data?.blockages || [];
            
            this.getLogoBase64((logoBase64) => {
                this.pdfService.generateBlockedRoomsReport(allData, logoBase64);
            });
        } catch (err) {
            console.error('Error fetching complete data for report:', err);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private getLogoBase64(callback: (base64: string) => void) {
        const logoPath = '/assets/images/logometasmile.png';
        fetch(logoPath)
            .then(response => response.blob())
            .then(blob => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64 = reader.result as string;
                    const base64Data = base64.split(',')[1] || base64;
                    callback(base64Data);
                };
                reader.readAsDataURL(blob);
            })
            .catch(() => {
                callback('');
            });
    }

    async onLazyLoad(event: any) {
        this.currentPage = Math.floor(event.first / event.rows) + 1;
        this.pageSize = event.rows;
        await this.loadBlockedRooms();
    }

    async applyFilters() {
        this.currentPage = 1;
        await this.loadBlockedRooms();
    }

    async clearFilters() {
        this.searchText = '';
        const { start, end } = this.getCurrentMonthRange();
        this.startDate = start;
        this.endDate = end;
        this.currentPage = 1;
        await this.loadBlockedRooms();
    }

    async filterToday() {
        const today = new Date();
        this.startDate = today;
        this.endDate = today;
        this.currentPage = 1;
        await this.loadBlockedRooms();
    }

    async loadBlockedRooms() {
        if (this.isFetching) return;
        
        this.isFetching = true;
        this.loading = true;
        this.cdr.detectChanges();

        const offset = (this.currentPage - 1) * this.pageSize;

        try {
            const userInfo = await firstValueFrom(
                this.userInfo$.pipe(
                    filter(u => !!u),
                    take(1)
                )
            ) as any;

            const branchId = userInfo?.branches_id || userInfo?.branch_id;
            if (!branchId) {
                console.error('No branch ID found for user');
                this.loading = false;
                this.isFetching = false;
                this.cdr.detectChanges();
                return;
            }

            const sDate = this.startDate ? this.toDateOnlyString(this.startDate) : '';
            const eDate = this.endDate ? this.toDateOnlyString(this.endDate) : '';

            const { data, error } = await this.roomsService.getBlockedRooms(
                branchId,
                this.pageSize,
                offset,
                sDate,
                eDate,
                this.searchText
            );

            if (data) {
                this.blockedRooms = data.blockages || [];
                this.totalPages = data.total_pages || 0;
            } else {
                console.error('Error fetching blocked rooms', error);
                this.blockedRooms = [];
                this.totalPages = 0;
            }
        } catch (e) {
            console.error('Exception fetching blocked rooms', e);
        } finally {
            this.loading = false;
            this.isFetching = false;
            this.cdr.detectChanges();
        }
    }

    async onSearch(text: string) {
        this.searchText = text;
        this.currentPage = 1;
        await this.loadBlockedRooms();
    }

    async onDateChange() {
        this.currentPage = 1;
        await this.loadBlockedRooms();
    }

    /**
     * date.toISOString() converts to UTC first, which shifts the calendar
     * day for any timezone ahead of UTC (e.g. picking the 21st locally can
     * become "20" once converted to UTC). Use the date's own local
     * year/month/day instead so what the user clicked is what gets sent.
     */
    private toDateOnlyString(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
